/**
 * @fileoverview Business Portal Layout - Premium Washed Charcoal Aesthetic
 *
 * Main layout component for tenant business owners and managers.
 * Matches marketing site and sales portal design system.
 *
 * DESIGN SYSTEM:
 * - Background: #292929 (main), #2d2d2d, #2c2c2c (surfaces)
 * - Borders: white/[0.08], white/[0.05]
 * - Text: gray-100 (headings), gray-400 (body), gray-500 (labels)
 * - Accents: Lime #84CC16, Teal #14B8A6 at 70-80% opacity
 * - Sharp corners, premium shadows, alternating lime/teal nav items
 * - Mobile: Hamburger menu with slide-out drawer
 */

import { useState } from "react";
import { Link, useLocation, Outlet } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LayoutDashboard,
  Phone,
  Users,
  Calendar,
  Settings,
  LogOut,
  ChevronLeft,
  TrendingUp,
  ClipboardList,
  Wrench,
  HelpCircle,
  Sparkles,
  Briefcase,
  Menu,
  X,
} from "lucide-react";
import mynaLogo from "@/assets/myna-logo.svg";
import { useAuth } from "@/contexts/AuthContext";
import { PageErrorBoundary } from "@/components/ErrorBoundary";
import { KeyboardShortcutsHelp } from "@/components/KeyboardShortcutsHelp";
import { useKeyboardShortcuts } from "@/hooks/use-keyboard-shortcuts";
import { usePendingSuggestionsCount } from "@/hooks/use-dispatch-suggestions";
import { useHasFeature } from "@/hooks/use-tenant";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import type { TierFeature } from "@/lib/constants";

// Nav items with optional tier-based feature gating
interface NavItem {
  title: string;
  href: string;
  icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  showBadge?: boolean;
  requiredFeature?: TierFeature;
}

const navItems: NavItem[] = [
  { title: "Dashboard", href: "/app/dashboard", icon: LayoutDashboard },
  { title: "Calls", href: "/app/dashboard/calls", icon: Phone },
  { title: "Jobs", href: "/app/dashboard/jobs", icon: ClipboardList },
  { title: "Dispatch", href: "/app/dashboard/dispatch", icon: Sparkles, showBadge: true, requiredFeature: "smartDispatch" },
  { title: "Services", href: "/app/dashboard/services", icon: Briefcase },
  { title: "FAQs", href: "/app/dashboard/faqs", icon: HelpCircle },
  { title: "Customers", href: "/app/dashboard/customers", icon: Users },
  { title: "Technicians", href: "/app/dashboard/technicians", icon: Wrench, requiredFeature: "techPortal" },
  { title: "Analytics", href: "/app/dashboard/analytics", icon: TrendingUp },
  { title: "Schedule", href: "/app/dashboard/schedule", icon: Calendar },
];

const bottomNavItems = [
  { title: "Settings", href: "/app/dashboard/settings", icon: Settings },
  { title: "Help", href: "/app/dashboard/help", icon: HelpCircle },
];

/**
 * NavItemLink - Renders a nav item with tier-based feature gating.
 * If the item requires a feature the user doesn't have, it won't render.
 */
function NavItemLink({
  item,
  index,
  isActive,
  pendingCount,
}: {
  item: NavItem;
  index: number;
  isActive: boolean;
  pendingCount: number | undefined;
}) {
  // Check feature access - if no feature required, always show
  const hasFeatureAccess = useHasFeature(item.requiredFeature || "businessDashboard");

  // Hide nav item if feature is gated and user doesn't have access
  if (item.requiredFeature && !hasFeatureAccess) {
    return null;
  }

  const accentColor = index % 2 === 0 ? "lime" : "teal";
  const showBadge = item.showBadge && pendingCount && pendingCount > 0;

  return (
    <Link
      to={item.href}
      className={`flex items-center gap-3 px-4 py-3 transition-all border-l-2 group relative focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime-500/50 ${
        isActive
          ? accentColor === "lime"
            ? "bg-lime-500/10 text-lime-400 border-lime-500/60 shadow-lg shadow-lime-500/10"
            : "bg-teal-500/10 text-teal-400 border-teal-500/60 shadow-lg shadow-teal-500/10"
          : "text-gray-400 hover:text-gray-200 hover:bg-white/5 border-transparent hover:border-white/10"
      }`}
    >
      <item.icon
        className={`w-5 h-5 transition-transform group-hover:scale-110 ${
          isActive
            ? accentColor === "lime" ? "text-lime-400" : "text-teal-400"
            : "text-gray-500"
        }`}
        strokeWidth={isActive ? 2 : 1.5}
      />
      <span className="font-semibold text-sm tracking-wide">{item.title}</span>

      {/* Pending count badge */}
      {showBadge && (
        <span className="ml-auto bg-lime-500 text-black text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center animate-pulse">
          {pendingCount}
        </span>
      )}
    </Link>
  );
}

export function TenantLayout() {
  const location = useLocation();
  const { signOut, currentTenant } = useAuth();
  const { data: pendingCount } = usePendingSuggestionsCount();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { showHelp, setShowHelp } = useKeyboardShortcuts();

  const handleSignOut = async () => {
    await signOut();
    window.location.href = "/login";
  };

  const isActive = (href: string) => {
    if (href === "/app/dashboard") {
      return location.pathname === href;
    }
    return location.pathname.startsWith(href);
  };

  // Close mobile menu when navigating
  const handleMobileNavClick = () => {
    setMobileMenuOpen(false);
  };

  // Shared sidebar content for both desktop and mobile
  const SidebarContent = ({ onNavClick }: { onNavClick?: () => void }) => (
    <>
      {/* Logo */}
      <div className="p-6 border-b border-white/[0.08]">
        <Link to="/app/dashboard" className="block" onClick={onNavClick}>
          <img src={mynaLogo} alt="MYNA AI" className="h-24 w-auto opacity-90 mb-2" />
          <div className="px-2 py-1 bg-teal-500/10 border border-teal-500/30 inline-block">
            <span className="text-xs text-teal-400/80 font-semibold tracking-wider uppercase">
              Business Portal
            </span>
          </div>
        </Link>
        {currentTenant?.tenant && (
          <div className="mt-4 pt-4 border-t border-white/[0.05]">
            <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-1">
              Business
            </p>
            <p className="text-sm text-gray-300 font-medium truncate">
              {currentTenant.tenant.name}
            </p>
          </div>
        )}
      </div>

      {/* Navigation - with tier-based feature gating */}
      <nav aria-label="Sidebar navigation" className="flex-1 min-h-0 p-4 space-y-1 overflow-y-auto">
        {navItems.map((item, index) => (
          <div key={item.href} onClick={onNavClick}>
            <NavItemLink
              item={item}
              index={index}
              isActive={isActive(item.href)}
              pendingCount={pendingCount}
            />
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-white/[0.08] space-y-1">
        {bottomNavItems.map((item, index) => {
          const active = isActive(item.href);
          const accentColor = index % 2 === 0 ? "lime" : "teal";

          return (
            <Link
              key={item.href}
              to={item.href}
              onClick={onNavClick}
              className={`flex items-center gap-3 px-4 py-3 transition-all border-l-2 group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime-500/50 ${
                active
                  ? accentColor === "lime"
                    ? "bg-lime-500/10 text-lime-400 border-lime-500/60"
                    : "bg-teal-500/10 text-teal-400 border-teal-500/60"
                  : "text-gray-400 hover:text-gray-200 hover:bg-white/5 border-transparent hover:border-white/10"
              }`}
            >
              <item.icon
                className={`w-5 h-5 transition-transform group-hover:scale-110 ${
                  active
                    ? accentColor === "lime" ? "text-lime-400" : "text-teal-400"
                    : "text-gray-500"
                }`}
                strokeWidth={active ? 2 : 1.5}
              />
              <span className="font-medium text-sm">{item.title}</span>
            </Link>
          );
        })}

        <Link
          to="/"
          onClick={onNavClick}
          className="flex items-center gap-3 px-4 py-3 text-gray-400 hover:text-gray-200 hover:bg-white/5 transition-all group border-l-2 border-transparent hover:border-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime-500/50"
        >
          <ChevronLeft className="w-5 h-5 text-gray-500 group-hover:text-teal-400 transition-colors" strokeWidth={1.5} />
          <span className="font-medium text-sm">Back to Website</span>
        </Link>
        <button
          onClick={() => {
            onNavClick?.();
            handleSignOut();
          }}
          className="flex items-center gap-3 px-4 py-3 text-gray-400 hover:text-gray-200 hover:bg-white/5 transition-all group w-full text-left border-l-2 border-transparent hover:border-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime-500/50"
        >
          <LogOut className="w-5 h-5 text-gray-500 group-hover:text-red-400 transition-colors" strokeWidth={1.5} />
          <span className="font-medium text-sm">Sign Out</span>
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#292929] text-white flex flex-col md:flex-row">
      {/* Skip to main content link for keyboard users */}
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:top-4 focus:left-4 focus:px-4 focus:py-2 focus:bg-lime-500 focus:text-black focus:font-bold focus:rounded-lg">
        Skip to main content
      </a>

      {/* Gradient wash background */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-b from-[#2e2e2e] via-[#292929] to-[#282828]" />
        <div
          className="absolute top-1/4 left-1/4 w-[600px] h-[600px]"
          style={{
            background: "radial-gradient(circle, rgba(132,204,22,0.015) 0%, transparent 70%)",
            filter: "blur(120px)",
          }}
        />
      </div>

      {/* Mobile Header - Only visible on mobile */}
      <header className="md:hidden relative z-20 bg-[#2d2d2d] border-b border-white/[0.08] px-4 py-3 flex items-center justify-between">
        <Link to="/app/dashboard" className="flex items-center gap-3">
          <img src={mynaLogo} alt="MYNA AI" className="h-16 w-auto opacity-90" />
        </Link>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setMobileMenuOpen(true)}
          className="text-gray-400 hover:text-white hover:bg-white/10"
          aria-expanded={mobileMenuOpen}
          aria-label="Open menu"
        >
          <Menu className="h-6 w-6" />
        </Button>
      </header>

      {/* Mobile Navigation Drawer */}
      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent
          side="left"
          className="w-[280px] h-full p-0 bg-[#2d2d2d] border-r border-white/[0.08] flex flex-col overflow-hidden"
        >
          <SheetHeader className="sr-only">
            <SheetTitle>Navigation Menu</SheetTitle>
          </SheetHeader>
          <SidebarContent onNavClick={handleMobileNavClick} />
        </SheetContent>
      </Sheet>

      {/* Desktop Sidebar - Hidden on mobile */}
      <aside className="hidden md:flex relative z-10 w-64 bg-[#2d2d2d] border-r border-white/[0.08] flex-col shadow-2xl">
        <SidebarContent />
      </aside>

      {/* Main Content */}
      <main id="main-content" className="relative z-10 flex-1 overflow-auto">
        <div className="max-w-[1600px] mx-auto p-4 sm:p-6 md:p-8 lg:p-12">
          <PageErrorBoundary>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Outlet />
            </motion.div>
          </PageErrorBoundary>
        </div>
      </main>

      {/* Keyboard Shortcuts Help */}
      <KeyboardShortcutsHelp open={showHelp} onClose={() => setShowHelp(false)} />
    </div>
  );
}
